import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { lowerCase, startCase } from 'lodash';
import { parseDateOrDateTime } from '../../utils';
import Wrapper from '../wrapper/Wrapper';
import Divider from '../divider/Divider';
import Loader from '../loader/Loader';
import * as styles from './EcbCalculator.module.scss';
import { convertNumberToCurrency } from '../../utils';

const EcbCalculator = () => {
  const [date, setDate] = useState('');
  const [currencies, setCurrencies] = useState([]);
  const [amount, setAmount] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [ecbDetails, setEcbDetails] = useState({});
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [loading, setLoading] = useState(true);

  const ERROR_WRONG = 'Something went wrong, please try again.';
  const ERROR_DATE = 'Please select a date within the last year.';
  const ERROR_PRICE = `We’re sorry, we can’t get a price for you at the moment, please try again later.`;
  const ERROR_400 =
    'Something went wrong, please try again! Please ensure that you have selected a date within the past year.';

  // Onpage load add api call to pull through currencies, on use effect, store in state
  useEffect(() => {
    axios
      .get(window.starlingbank.getConfig('ecbCurrencies'))
      .then((data) => {
        setCurrencies(data.data);
      })
      .catch(() => {
        setErrorMsg(ERROR_WRONG);
      });
    setLoading(false);
  }, []);

  // API request, submits card currency, target currency, and amount. aiming to get the rate for above calculation
  const fetchEcbRate = (e) => {
    e.preventDefault();

    setLoading(true);
    setEcbDetails({});

    axios
      .get(
        `${window.starlingbank.getConfig(
          'ecbCalculator'
        )}?cardCurrency=GBP&transactionCurrency=${selectedCurrency}&amount=${amount}&transactionDate=${date}&bankFeePct=0&bankFeeFixed=0`
      )
      .then(({ data: response }) => {
        if (!response.crdhldBillAmtExclAllFees && !response.effectiveConversionRate && !response.rateDate) {
          setLoading(false);
          setErrorMsg(ERROR_WRONG);
        } else {
          setErrorMsg('');
          setEcbDetails(response);
          setLoading(false);
        }
      })
      .catch((error) => {
        if (error.response.status === 404 || error.response.status === 403 || error.response.status === 400) {
          setErrorMsg(ERROR_400);
        } else if (error.response.status === 500) {
          setErrorMsg(ERROR_PRICE);
        } else {
          setErrorMsg(ERROR_WRONG);
        }
      });
  };

  const calculatorMinDate = () => {
    let date = new Date();
    date.setFullYear(date.getFullYear() - 1);
    date.setDate(date.getDate() + 1);
    return date.toJSON().split('T')[0];
  };

  const isSelectedDateInRange = () => {
    let newDate = new Date();
    let currentDate = new Date().getTime();
    let oneYearAgo = newDate.setDate(newDate.getDate() - 366);
    let selectedDate = new Date(date).getTime();

    return oneYearAgo <= selectedDate && selectedDate <= currentDate;
  };

  const isInvalidCurrencySelected = () => {
    return selectedCurrency === 'Select' || selectedCurrency === '';
  };

  const formatDate = (str) => {
    const thisDate = new Date(str);
    return parseDateOrDateTime(thisDate).format('Do MMMM YYYY');
  };

  return (
    <Wrapper section paddingTop="lg" paddingBottom="lg">
      <div className={styles.exchangeCalculator}>
        <div className={styles.exchangeCalculatorInner}>
          <div className={styles.content}>
            <div className={styles.intro}>
              <h3 className={styles.title}>Currency calculator</h3>
              <p>
                Need to convert GBP to USD or EUR? Use our currency converter below to calculate the exchange rate. We
                won’t charge you for spending overseas – but do bear in mind that some ATM providers may charge their
                own fee.
              </p>
              <p>
                Already in paradise? Use our currency converter on the go. Just go to <b>Cards</b> in the app, then tap
                on <b>‘Going abroad?’.</b>
              </p>
            </div>

            {currencies.length === 0 ? (
              <Loader />
            ) : (
              <form className={styles.form} onSubmit={(e) => fetchEcbRate(e)}>
                <div className={styles.formBody}>
                  <div>
                    <label htmlFor="ecb-currency">Select currency </label>
                    <select
                      id="ecb-currency"
                      value={selectedCurrency}
                      onChange={(e) => {
                        setSelectedCurrency(e.target.value);
                        setEcbDetails({});
                      }}
                      onBlur={(e) => setSelectedCurrency(e.target.value)}
                    >
                      {' '}
                      <option>Select</option>
                      {currencies.map((currency) => (
                        <option value={currency.code} key={currency.code}>
                          {startCase(lowerCase(currency.name))}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="ecb-transaction">Enter transaction amount </label>
                    <input
                      id="ecb-transaction"
                      className="exchange-calculator--transaction-input"
                      type="number"
                      placeholder="e.g. 1000"
                      value={amount}
                      min={1}
                      onChange={(event) => {
                        setAmount(event.target.value);
                        setEcbDetails({});
                      }}
                    />
                  </div>
                  <div>
                    <label htmlFor="ecb-date">Select date of transaction: </label>
                    <input
                      id="ecb-date"
                      className="exchange-calculator__date-picker"
                      value={date}
                      type="date"
                      min={calculatorMinDate()}
                      max={new Date().toJSON().split('T')[0]}
                      onChange={(event) => {
                        setDate(event.target.value);
                        setEcbDetails({});
                      }}
                    />
                  </div>
                </div>

                {!isSelectedDateInRange() && date !== '' && <p>{ERROR_DATE}</p>}
                <button
                  className="cta-button"
                  type="submit"
                  disabled={
                    !amount ||
                    !currencies ||
                    !date ||
                    !isSelectedDateInRange() ||
                    isInvalidCurrencySelected() ||
                    ecbDetails.billingAmountIncludingFees
                  }
                >
                  Calculate rate
                </button>
              </form>
            )}
          </div>

          <div className={styles.results}>
            {errorMsg && <p className={styles.error}>{errorMsg}</p>}
            {loading && !errorMsg && <Loader text="Calculating rate" />}

            {ecbDetails.billingAmountIncludingFees && !loading && (
              <div className={styles.resultsInner}>
                <h4>If you made this transaction on {formatDate(ecbDetails?.mastercardRateDate)} you would pay:</h4>
                <div className={styles.fees}>
                  <p className={styles.total}>
                    {convertNumberToCurrency(ecbDetails?.billingAmountIncludingFees, 'en-GB', 'GBP')}
                  </p>
                  <Divider />
                  <ul>
                    <li>Full precision amount: £{ecbDetails?.billingAmountIncludingFees}</li>
                    <li>Exchange rate: {ecbDetails?.effectiveConversionRate}</li>
                    <li>
                      {ecbDetails?.ecbReferenceRate ? 'ECB exchange rate: ' + ecbDetails?.ecbReferenceRate : null}
                    </li>
                    <li>
                      {ecbDetails?.percentDiffIncludingAllFees
                        ? 'Total markup over ECB rate: ' + ecbDetails?.percentDiffIncludingAllFees + '%'
                        : null}
                    </li>
                  </ul>
                  <Divider />
                  <h5>The fee Starling keeps would be £0.00</h5>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default EcbCalculator;
